import React, { useState, useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './styles/styles.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import LoginPage from './pages/LoginPage';
import BurgerMenu from './components/common/BurgerMenu';
import Dashboard from './components/common/Dashboard';
import TeamStatsTable from './components/football/TeamStatsTable';
import Championships from './components/football/Championships';
import LottoMain from './components/lotto/LottoMain';
import WinHauseMain from './components/winhause/WinHauseMain';
import SuperEnalottoMain from './components/superenalotto/SuperEnalottoMain';
import GameOnlineMenu from './components/games/GameOnlineMenu';
import UserProfile from './components/auth/UserProfile';
import UserProfileEdit from './components/auth/UserProfileEdit';
import Settings from './components/auth/Settings';
import FantasyMainPage from './pages/FantasyMainPage';
import FantasyPlayersPage from './pages/FantasyPlayersPage';
import FantasyStatsPage from './pages/FantasyStatsPage';
import FantasyFixturesPage from './pages/FantasyFixturesPage';
import FantasyLeaderboardPage from './pages/FantasyLeaderboardPage';
import { useTranslation } from 'react-i18next';
import './i18n/i18n';
import Select from 'react-select';
import WorldFlag from 'react-world-flags';

/**
 * Main App Component with Authentication
 * Manages authentication and tab navigation
 */
const AppContent = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [currentLanguage, setCurrentLanguage] = useState(localStorage.getItem('appLanguage') || 'it');
  const { i18n } = useTranslation();
  const { isAuthenticated, loading } = useAuth();

  // Listen for language changes
  useEffect(() => {
    const handleLanguageChanged = (lng) => {
      setCurrentLanguage(lng);
    };

    i18n.on('languageChanged', handleLanguageChanged);

    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [i18n]);

  // Show login page if not authenticated
  if (!isAuthenticated && !loading) {
    return <LoginPage />;
  }

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Handle language change
  const handleLanguageChange = (selectedOption) => {
    i18n.changeLanguage(selectedOption.value);
  };

  const tabs = [
    { label: 'Dashboard', index: 0 },
    {
      label: 'Football',
      category: true,
      submenu: [
        { label: 'Championships', index: 1 },
        { label: 'Team Stats', index: 2 },
        { label: 'Fantasy Football', index: 9, category: true, submenu: [
          { label: 'Players', index: 10 },
          { label: 'Stats', index: 11 },
          { label: 'Fixtures', index: 12 },
          { label: 'Leaderboard', index: 13 }
        ]}
      ]
    },
    {
      label: 'Lottery',
      category: true,
      submenu: [
        { label: 'Lotto', index: 3 },
        { label: 'Win House', index: 4 },
        { label: 'Super Enalotto', index: 5 }
      ]
    },
    { label: 'Game Online', index: 6 }
  ];

  return (
    <div className="app-container">
      {/* Navigation Menu */}
      <BurgerMenu tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab} />

      {/* Main Content */}
      <div className="app-main-content">
        {/* Header with Language & User Controls */}
        <header className="app-header" role="banner">
          <div className="d-flex justify-content-end align-items-center gap-3 flex-wrap">
            {/* Right Side - Language Selector and User Profile */}
            <nav className="d-flex align-items-center gap-2" aria-label="User controls">
              {/* Language Selector */}
              <div className="language-selector">
                <label htmlFor="language-select" className="visually-hidden">
                  Select Language
                </label>
                <Select
                  inputId="language-select"
                  options={[
                    { value: 'it', label: <WorldFlag code="IT" width="25" height="15" aria-label="Italian" /> },
                    { value: 'en', label: <WorldFlag code="GB" width="25" height="15" aria-label="English" /> },
                  ]}
                  onChange={handleLanguageChange}
                  value={
                    currentLanguage === 'en'
                      ? { value: 'en', label: <WorldFlag code="GB" width="25" height="15" aria-label="English" /> }
                      : { value: 'it', label: <WorldFlag code="IT" width="25" height="15" aria-label="Italian" /> }
                  }
                  className="language-select-container"
                  aria-label="Language selection"
                  isSearchable={false}
                  styles={{
                    control: (base) => ({
                      ...base,
                      display: 'flex',
                      alignItems: 'center',
                      paddingLeft: '8px',
                      minWidth: '50px',
                      backgroundColor: 'transparent',
                      border: 'none',
                      boxShadow: 'none',
                      cursor: 'pointer'
                    }),
                    indicatorSeparator: () => ({ display: 'none' }),
                    singleValue: (base) => ({
                      ...base,
                      display: 'flex',
                      alignItems: 'center',
                    }),
                    menu: (base) => ({
                      ...base,
                      zIndex: 1000
                    })
                  }}
                />
              </div>

              {/* User Profile Dropdown */}
              <UserProfile setActiveTab={setActiveTab} />
            </nav>
          </div>
        </header>

        {/* Main Content Area */}
        <main className="app-content" role="main">
          {activeTab === 0 && <Dashboard activeTab={activeTab} setActiveTab={setActiveTab} />}
          {activeTab === 1 && <Championships />}
          {activeTab === 2 && <TeamStatsTable />}
          {activeTab === 3 && <LottoMain />}
          {activeTab === 4 && <WinHauseMain />}
          {activeTab === 5 && <SuperEnalottoMain />}
          {activeTab === 6 && <GameOnlineMenu />}
          {activeTab === 7 && <Settings />}
          {activeTab === 8 && <UserProfileEdit setActiveTab={setActiveTab} />}
          {activeTab === 9 && <FantasyMainPage setActiveTab={setActiveTab} />}
          {activeTab === 10 && <FantasyPlayersPage setActiveTab={setActiveTab} />}
          {activeTab === 11 && <FantasyStatsPage setActiveTab={setActiveTab} />}
          {activeTab === 12 && <FantasyFixturesPage setActiveTab={setActiveTab} />}
          {activeTab === 13 && <FantasyLeaderboardPage setActiveTab={setActiveTab} />}
        </main>
      </div>
    </div>
  );
};

/**
 * Main App Wrapper with AuthProvider and BrowserRouter
 */
const App = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;
